import {
  MainImage,
  MediaItem,
  Avatar,
  TemplateState,
  Logo,
  Header,
  CustomText,
  Chapter,
  Shape,
  Table,
  TableTextStyle,
  TableDividerStyle,
  Templates,
  TemplateType,
  AvatarShape,
  Size,
  ParameteredText,
  FigureType,
  ITEM_TYPES,
} from "../types";
import { isEqual } from "lodash";
import { createStore, useStore } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { temporal } from "zundo";
import { adjustTemplateToAspectRatioChange } from "../helpers/scaleTemplate";
import {
  computeAvatarLayoutForRatio,
  fitPositionToSize,
  getLayerableCollectionItems,
  withFixedScriptIfNeeded,
} from "../helpers";
import { useContext } from "react";
import { TemplateEditorStoreContext } from "providers/TemplateDataProvider/";
import { MediaTemplateResponse } from "types/";
import { PresenterProfileResponse } from "pages/media/types";
import {
  DEFAULT_HEADER_SIZE,
  DEFAULT_LOGO_SIZE,
  DEFAULT_POSITION,
  DEFAULT_PRESENTER_POSITION,
  DEFAULT_PRESENTER_SIZE,
  DEFAULT_SIZE_WITH_NULL_HEIGHT,
} from "../constants";

export type SelectionSource = "thumbnail" | "scene" | "script";

export type TemplateEditorProps = {
  currentChapter: Chapter;
  currentSlide?: string;
  selectionSource?: SelectionSource;
  selectedItemIds?: string[];
  isInteracting?: boolean;
  templateState: TemplateState;
  templates: Templates;
  serverTemplates: Map<string, MediaTemplateResponse["template"]> | undefined;
  slideNameToTemplateIdMap: Record<string, string>;
  defaultChapter: TemplateState;
  defaultTransition: TemplateState;
  defaultIntro: TemplateState;
  defaultClosing: TemplateState;
};

const DEFAULT_TEXT = {
  fontSize: 16,
  position: DEFAULT_POSITION,
  size: DEFAULT_SIZE_WITH_NULL_HEIGHT,
  textAlign: "center" as const,
  lineHeight: 1.5,
  layerIndex: 0,
};

const DEFAULT_PRESENTER = {
  avatarShot: "Medium" as const,
  size: DEFAULT_PRESENTER_SIZE,
  position: DEFAULT_PRESENTER_POSITION,
  avatarShape: AvatarShape.Regular,
  layerIndex: 0,
};

const DEFAULT_VIDEO_ITEM = {
  enable: false,
  view: "Video" as const,
};

const DEFAULT_LOGO = {
  showLogo: false,
  position: DEFAULT_POSITION,
  size: DEFAULT_LOGO_SIZE,
  layerIndex: 0,
};

const DEFAULT_HEADER = {
  view: "Color" as const,
  position: DEFAULT_POSITION,
  size: DEFAULT_HEADER_SIZE,
  layerIndex: 0,
};

const DEFAULT_BACKGROUND = {
  bodyView: "Color" as const,
};

export const DEFAULT_TEMPLATE_STATE = {
  textStyle: {
    ...DEFAULT_TEXT,
    animated: true,
    showSubtitles: false,
  },
  titleStyle: {
    ...DEFAULT_TEXT,
    showTitle: false,
  },
  presenter: DEFAULT_PRESENTER,
  intro: DEFAULT_VIDEO_ITEM,
  transition: DEFAULT_VIDEO_ITEM,
  closing: DEFAULT_VIDEO_ITEM,
  logo: DEFAULT_LOGO,
  header: DEFAULT_HEADER,
  background: DEFAULT_BACKGROUND,
  images: [],
  videos: [],
  shapes: [],
  customTexts: [],
};

const DEFAULT_PROPS: TemplateEditorProps = {
  currentChapter: {
    sectionId: "",
    topic: "",
    type: TemplateType.CHAPTER,
  },
  templateState: DEFAULT_TEMPLATE_STATE,
  selectedItemIds: [],
  isInteracting: false,
  templates: new Map(),
  serverTemplates: new Map(),
  slideNameToTemplateIdMap: {},
  defaultChapter: DEFAULT_TEMPLATE_STATE,
  defaultTransition: DEFAULT_TEMPLATE_STATE,
  defaultIntro: DEFAULT_TEMPLATE_STATE,
  defaultClosing: DEFAULT_TEMPLATE_STATE,
};

export type TemplateEditorState = {
  currentChapter: Chapter;
  currentSlide?: string;
  selectionSource?: SelectionSource;
  /**
   * Keys (item.id ?? item.type) of all items in the current multi-selection.
   * Single-selection keeps this in sync with `templateState.currentItem`;
   * length > 1 means a group selection (per-item settings are hidden).
   */
  selectedItemIds: string[];
  /** True while an item is being dragged/resized/rotated (suppresses hover). */
  isInteracting: boolean;
  templateState: TemplateState;
  templates: Templates;
  serverTemplates: Map<string, MediaTemplateResponse["template"]> | undefined;
  slideNameToTemplateIdMap: Record<string, string>;
  defaultChapter: TemplateState;
  defaultTransition: TemplateState;
  defaultIntro: TemplateState;
  defaultClosing: TemplateState;
  setCurrentChapter: (
    currentChapter: Chapter,
    source?: SelectionSource
  ) => void;
  setCurrentChapterTemplateId: (templateId: string) => void;
  setCurrentSlide: (id: string, source?: SelectionSource) => void;
  setTemplateState: (state: TemplateState) => void;
  updateTemplateState: (state: Partial<TemplateState>) => void;
  setTemplate: (id: string, template: TemplateState) => void;
  setServerTemplate: (
    id: string,
    template: MediaTemplateResponse["template"]
  ) => void;
  setTemplates: (ids: string[], template: Partial<TemplateState>) => void;
  deleteTemplate: (templateId: string) => void;
  addSlideToTemplateMap: (slideUuid: string, templateId: string) => void;
  removeSlideFromTemplateMap: (slideUuid: string) => void;
  changeTemplatesAspectRatio: (
    newAspectRatio: number,
    previewSizes: Size
  ) => void;
  setTextStyle: (newTextStyle: Partial<TemplateState["textStyle"]>) => void;
  setTitleStyle: (newTextStyle: Partial<TemplateState["titleStyle"]>) => void;
  setPresenter: (newPresenter: Partial<TemplateState["presenter"]>) => void;
  applyAvatarIdentityToAllChapters: (
    newPresenter: Partial<TemplateState["presenter"]>,
    mediaAspectRatio?: number
  ) => void;
  syncPresenterToOtherTemplatesInChapter: (
    newPresenter: Partial<TemplateState["presenter"]>,
    otherTemplateIdsInChapter: string[],
    mediaAspectRatio?: number
  ) => void;
  applyPresenterStyleToTemplates: (
    templateIds: string[],
    presenter: Avatar,
    targets: {
      identityByTemplateId: Map<string, PresenterProfileResponse>;
      transitionTemplateIds: Set<string>;
      presenterRatiosById?: Map<string, number>;
      mediaAspectRatio?: number;
    }
  ) => void;
  setIntro: (updatedIntro: Partial<TemplateState["intro"]>) => void;
  setTransition: (
    updatedTransition: Partial<TemplateState["transition"]>
  ) => void;
  setClosing: (updatedClosing: Partial<TemplateState["closing"]>) => void;
  updateMainImage: (update: Partial<MainImage>) => void;
  deleteMainImage: () => void;
  addImage: (newImage: MediaItem) => void;
  updateImage: (update: Partial<MediaItem>) => void;
  deleteImage: (id: string) => void;
  setImages: (images: MediaItem[]) => void;
  addVideo: (newVideo: MediaItem) => void;
  updateVideo: (update: Partial<MediaItem>) => void;
  deleteVideo: (id: string) => void;
  setVideos: (videos: MediaItem[]) => void;
  setBackground: (color: Partial<TemplateState["background"]>) => void;
  setCurrentItem: (item: TemplateState["currentItem"]) => void;
  setSelection: (ids: string[]) => void;
  toggleSelection: (id: string) => void;
  clearSelection: () => void;
  setIsInteracting: (isInteracting: boolean) => void;
  updateLogo: (logo: Partial<Logo>) => void;
  updateHeader: (header: Partial<Header>) => void;
  updateParameteredText: (textItem: Partial<ParameteredText>) => void;
  addParameteredTexts: (texts: ParameteredText[]) => void;
  updateFigure: (
    figureItem: Partial<ParameteredText>,
    figureType: FigureType
  ) => void;
  setCustomTexts: (customTexts: CustomText[]) => void;
  updateCustomText: (textItem: Partial<CustomText>) => void;
  addCustomText: (customText: CustomText) => void;
  deleteCustomText: (id: string) => void;
  setMusic: (music: TemplateState["music"]) => void;
  setShapes: (shapes: Shape[]) => void;
  updateShape: (update: Partial<Shape>) => void;
  addShape: (shapeItem: Shape) => void;
  deleteShape: (id: string) => void;
  addSlideLayoutShape: (shapeItem: Shape) => void;
  deleteSlideLayoutShape: (id: string) => void;
  updateTable: (update: Partial<Table>) => void;
  updateTableTextStyle: (style: Partial<TableTextStyle>) => void;
  updateTableDividerStyle: (style: Partial<TableDividerStyle>) => void;
  remapBrandKit: (params: {
    defaultChapter: TemplateState;
    defaultTransition: TemplateState;
    defaultIntro: TemplateState;
    defaultClosing: TemplateState;
    remappedTemplates: Map<string, TemplateState>;
  }) => void;
};

export type TemplateEditorStore = ReturnType<typeof createTemplateEditorStore>;

export const createTemplateEditorStore = (initProps?: TemplateEditorProps) => {
  return createStore<TemplateEditorState>()(
    devtools(
      temporal(
        immer((set) => {
          return {
            ...(initProps ?? DEFAULT_PROPS),
            selectedItemIds: (initProps ?? DEFAULT_PROPS).selectedItemIds ?? [],
            isInteracting: (initProps ?? DEFAULT_PROPS).isInteracting ?? false,
            setCurrentChapter: (currentChapter, source) =>
              set(
                (state) => {
                  state.currentChapter = currentChapter;
                  state.selectionSource = source;
                  state.selectedItemIds = [];
                  // Clear currentSlide when selecting a chapter/template (including intro/closing)
                  state.currentSlide = undefined;
                  const templateType = currentChapter.type;
                  const templateId = currentChapter.templateId;
                  if (templateId) {
                    const template =
                      state.templates.get(templateId) ??
                      selectDefaultTemplateByType(state, templateType);
                    state.templateState = {
                      ...template,
                      currentItem: undefined,
                    };
                  } else {
                    state.templateState = {
                      ...selectDefaultTemplateByType(state, templateType),
                      currentItem: undefined,
                    };
                  }
                },
                undefined,
                "setCurrentChapter"
              ),
            setCurrentChapterTemplateId: (templateId) =>
              set(
                (state) => {
                  state.currentChapter = {
                    ...state.currentChapter,
                    templateId,
                  };
                  state.templates.set(templateId, { ...state.templateState });
                },
                undefined,
                "setCurrentChapterTemplateId"
              ),
            setCurrentSlide: (id, source) =>
              set(
                (state) => {
                  state.currentSlide = id;
                  state.selectionSource = source;
                  state.selectedItemIds = [];
                  // Reset currentChapter to default when selecting a slide
                  state.currentChapter = {
                    templateId: undefined,
                    topic: "",
                    sectionId: "",
                    type: TemplateType.CHAPTER,
                  };

                  const template = state.templates.get(id);
                  if (!template) return;
                  state.templateState = {
                    ...template,
                    currentItem: undefined,
                  };
                },
                undefined,
                "setCurrentSlide"
              ),
            setTemplateState: (newState) =>
              set(
                (state) => {
                  state.templateState = newState;
                },
                undefined,
                "setTemplateState"
              ),
            updateTemplateState: (templateUpdates) =>
              set(
                (state) => {
                  const newTemplateState = {
                    ...state.templateState,
                    ...templateUpdates,
                  };
                  syncTemplateStateWithTemplates(state, newTemplateState);
                },
                undefined,
                "updateTemplateState"
              ),
            setTemplate: (id, template) =>
              set(
                (state) => {
                  state.templates.set(id, template);
                },
                undefined,
                "setTemplate"
              ),
            // Keep the server-template snapshot in sync with what we last wrote
            // to the server. It's the read-modify-write base for template saves
            // (add/remove slide, save slide, bulk save, apply theme); if it goes
            // stale, those writes drop or resurrect slides changed this session.
            setServerTemplate: (id, template) =>
              set(
                (state) => {
                  state.serverTemplates ??= new Map();
                  state.serverTemplates.set(id, template);
                },
                undefined,
                "setServerTemplate"
              ),
            setTemplates: (ids, templateUpdates) =>
              set(
                (state) => {
                  ids.forEach((id) => {
                    const template = state.templates.get(id);
                    if (template) {
                      state.templates.set(id, {
                        ...template,
                        ...templateUpdates,
                      });
                    } else {
                      state.templates.set(id, {
                        ...state.templateState,
                        ...templateUpdates,
                      });
                    }
                  });
                },
                undefined,
                "setTemplates"
              ),
            deleteTemplate: (templateId: string) =>
              set(
                (state) => {
                  state.templates.delete(templateId);
                },
                undefined,
                "deleteTemplate"
              ),
            addSlideToTemplateMap: (slideUuid, templateId) =>
              set(
                (state) => {
                  state.slideNameToTemplateIdMap[slideUuid] = templateId;
                },
                undefined,
                "addSlideToTemplateMap"
              ),
            removeSlideFromTemplateMap: (slideUuid) =>
              set(
                (state) => {
                  delete state.slideNameToTemplateIdMap[slideUuid];
                },
                undefined,
                "removeSlideFromTemplateMap"
              ),
            changeTemplatesAspectRatio: (newAspectRatio, previewSizes) => {
              set(
                (state) =>
                  changeTemplatesAspectRatioCallback(
                    state,
                    newAspectRatio,
                    previewSizes
                  ),
                undefined,
                "changeTemplatesAspectRatio"
              );
            },
            setPresenter: (newPresenterPartial: Partial<Avatar>) => {
              set(
                (state) =>
                  applyPresenterToCurrentTemplate(state, newPresenterPartial),
                undefined,
                "setPresenter"
              );
            },
            applyAvatarIdentityToAllChapters: (
              newPresenterPartial,
              mediaAspectRatio
            ) => {
              set(
                (state) => {
                  applyPresenterToCurrentTemplate(state, newPresenterPartial);
                  applyPresenterContactToOtherTemplates(
                    state,
                    newPresenterPartial,
                    mediaAspectRatio
                  );
                },
                undefined,
                "applyAvatarIdentityToAllChapters"
              );
            },
            // Presenter identity is a per-chapter setting (mediaGenerationSettings
            // .chapters[].presenter_id) even though each slide/transition keeps its
            // own TemplateState — so a change made on one of a chapter's scenes must
            // propagate to the chapter's other scenes that currently show an avatar
            // (the current scene itself is updated separately via setPresenter).
            syncPresenterToOtherTemplatesInChapter: (
              newPresenterPartial,
              otherTemplateIdsInChapter,
              mediaAspectRatio
            ) => {
              set(
                (state) => {
                  applyPresenterContactToOtherTemplates(
                    state,
                    newPresenterPartial,
                    mediaAspectRatio,
                    new Set(otherTemplateIdsInChapter)
                  );
                },
                undefined,
                "syncPresenterToOtherTemplatesInChapter"
              );
            },
            applyPresenterStyleToTemplates: (
              templateIds,
              presenter,
              {
                identityByTemplateId,
                transitionTemplateIds,
                presenterRatiosById,
                mediaAspectRatio,
              }
            ) => {
              // Position and layer order depend on each scene's own layout, so keep them.
              const {
                presenter: identity,
                contactId: _contactId,
                position: _position,
                layerIndex: _layerIndex,
                ...styleFields
              } = presenter;
              const isShown = identity !== undefined;
              set(
                (state) => {
                  templateIds.forEach((id) => {
                    const template = state.templates.get(id);
                    if (!template) return;
                    const wasHidden = template.presenter.presenter === undefined;
                    // Identity is per chapter: reuse the scene's own, or its chapter's.
                    const nextIdentity = isShown
                      ? (template.presenter.presenter ??
                        identityByTemplateId.get(id))
                      : undefined;
                    if (isShown && !nextIdentity) return;
                    const becomesVisible = wasHidden && isShown;

                    const fittedPosition = fitPositionToSize(
                      template.presenter.size,
                      template.presenter.position,
                      styleFields.size
                    );
                    // Regular shows the whole image, so refit the width to this scene's own avatar image.
                    const imageRatio = nextIdentity
                      ? presenterRatiosById?.get(nextIdentity.id)
                      : undefined;
                    const layout =
                      styleFields.avatarShape === AvatarShape.Regular &&
                      imageRatio != null &&
                      mediaAspectRatio != null
                        ? computeAvatarLayoutForRatio(
                            styleFields.size,
                            fittedPosition,
                            imageRatio,
                            mediaAspectRatio
                          )
                        : { size: styleFields.size, position: fittedPosition };

                    state.templates.set(id, {
                      ...template,
                      presenter: {
                        ...template.presenter,
                        ...styleFields,
                        presenter: nextIdentity,
                        size: layout.size,
                        position: layout.position,
                        // Bring a newly shown avatar to the front, like the visibility switch.
                        ...(becomesVisible && {
                          layerIndex: getNextLayerIndex(template),
                        }),
                      },
                      ...(becomesVisible &&
                        transitionTemplateIds.has(id) && {
                          transition: withFixedScriptIfNeeded(
                            template.transition,
                            true
                          ),
                        }),
                    });
                  });
                },
                undefined,
                "applyPresenterStyleToTemplates"
              );
            },
            setTextStyle: (property) =>
              set(
                (state) => {
                  const templateState = state.templateState;
                  const newTextStyle = {
                    ...templateState.textStyle,
                    ...property,
                  };

                  if (!isEqual(templateState.textStyle, newTextStyle)) {
                    templateState.textStyle = newTextStyle;
                    syncTemplateStateWithTemplates(state, templateState);
                  }
                },
                undefined,
                "setTextStyle"
              ),
            setTitleStyle: (property) =>
              set(
                (state) => {
                  const templateState = state.templateState;
                  const newTitleStyle = {
                    ...templateState.titleStyle,
                    ...property,
                  };
                  if (!isEqual(templateState.titleStyle, newTitleStyle)) {
                    templateState.titleStyle = newTitleStyle;
                    syncTemplateStateWithTemplates(state, templateState);
                  }
                },
                undefined,
                "setTitleStyle"
              ),
            setIntro: (updatedIntro) =>
              set(
                (state) => {
                  const templateState = state.templateState;
                  const intro = { ...templateState.intro };
                  if (
                    updatedIntro.assetId &&
                    updatedIntro.assetId !== intro.assetId
                  ) {
                    intro.assetPath = undefined;
                  }

                  const newIntro = {
                    ...intro,
                    ...updatedIntro,
                  };

                  if (!isEqual(templateState.intro, newIntro)) {
                    templateState.intro = newIntro;
                    syncTemplateStateWithTemplates(state, templateState);
                  }
                },
                undefined,
                "setIntro"
              ),
            setTransition: (updatedTransition) =>
              set(
                (state) => {
                  const templateState = state.templateState;
                  const transition = { ...templateState.transition };
                  if (
                    updatedTransition.assetId &&
                    updatedTransition.assetId !== transition.assetId
                  ) {
                    transition.assetPath = undefined;
                  }

                  const newTransition = {
                    ...transition,
                    ...updatedTransition,
                  };
                  if (!isEqual(templateState.transition, newTransition)) {
                    templateState.transition = newTransition;
                    syncTemplateStateWithTemplates(state, templateState);
                  }
                },
                undefined,
                "setTransition"
              ),
            setClosing: (updatedClosing) =>
              set(
                (state) => {
                  const templateState = state.templateState;
                  const closing = { ...templateState.closing };
                  if (
                    updatedClosing.assetId &&
                    updatedClosing.assetId !== closing.assetId
                  ) {
                    closing.assetPath = undefined;
                  }

                  const newClosing = {
                    ...closing,
                    ...updatedClosing,
                  };
                  if (!isEqual(templateState.closing, newClosing)) {
                    templateState.closing = newClosing;
                    syncTemplateStateWithTemplates(state, templateState);
                  }
                },
                undefined,
                "setClosing"
              ),
            setBackground: (updatedProperties) =>
              set(
                (state) => {
                  const templateState = state.templateState;
                  const background = { ...templateState.background };

                  const newBackground = {
                    ...background,
                    ...updatedProperties,
                  };
                  if (!isEqual(background, newBackground)) {
                    templateState.background = newBackground;
                    syncTemplateStateWithTemplates(state, templateState);
                  }
                },
                undefined,
                "setBackground"
              ),
            updateMainImage: (update) => {
              set(
                (state) => {
                  const templateState = state.templateState;
                  templateState.mainImage = {
                    ...(templateState.mainImage ?? ({} as MainImage)),
                    ...update,
                  };
                  syncTemplateStateWithTemplates(state, templateState);
                },
                undefined,
                "updateMainImage"
              );
            },
            deleteMainImage: () => {
              set(
                (state) => {
                  const templateState = state.templateState;
                  templateState.mainImage = undefined;
                  syncTemplateStateWithTemplates(state, templateState);
                },
                undefined,
                "deleteMainImage"
              );
            },
            addImage: (newImage) => {
              set(
                (state) => {
                  const templateState = state.templateState;
                  templateState.images.push(newImage);
                  templateState.currentItem = {
                    type: ITEM_TYPES.Image,
                    id: newImage.id,
                  };
                  syncTemplateStateWithTemplates(state, templateState);
                },
                undefined,
                "addImage"
              );
            },
            updateImage: (update) => {
              set(
                (state) => {
                  const templateState = state.templateState;
                  templateState.images = templateState.images.map((image) =>
                    image.id === update.id ? { ...image, ...update } : image
                  );
                  syncTemplateStateWithTemplates(state, templateState);
                },
                undefined,
                "updateImage"
              );
            },
            deleteImage: (id) => {
              set(
                (state) => {
                  const templateState = state.templateState;
                  templateState.images = templateState.images.filter(
                    (logo) => logo.id !== id
                  );
                  syncTemplateStateWithTemplates(state, templateState);
                },
                undefined,
                "deleteImage"
              );
            },
            setImages: (images) => {
              set(
                (state) => {
                  const templateState = state.templateState;
                  templateState.images = images;
                  syncTemplateStateWithTemplates(state, templateState);
                },
                undefined,
                "setImages"
              );
            },
            addVideo: (newVideo) => {
              set(
                (state) => {
                  const templateState = state.templateState;
                  templateState.videos.push(newVideo);
                  templateState.currentItem = {
                    type: ITEM_TYPES.Video,
                    id: newVideo.id,
                  };
                  syncTemplateStateWithTemplates(state, templateState);
                },
                undefined,
                "addVideo"
              );
            },
            updateVideo: (update) => {
              set(
                (state) => {
                  const templateState = state.templateState;
                  templateState.videos = templateState.videos.map((video) =>
                    video.id === update.id ? { ...video, ...update } : video
                  );
                  syncTemplateStateWithTemplates(state, templateState);
                },
                undefined,
                "updateVideo"
              );
            },
            deleteVideo: (id) => {
              set(
                (state) => {
                  const templateState = state.templateState;
                  templateState.videos = templateState.videos.filter(
                    (logo) => logo.id !== id
                  );
                  syncTemplateStateWithTemplates(state, templateState);
                },
                undefined,
                "deleteVideo"
              );
            },
            setVideos: (videos) => {
              set(
                (state) => {
                  const templateState = state.templateState;
                  templateState.videos = videos;
                  syncTemplateStateWithTemplates(state, templateState);
                },
                undefined,
                "setVideos"
              );
            },
            setCurrentItem: (currentItem) => {
              set(
                (state) => {
                  state.templateState.currentItem = currentItem;
                  // Single selection follows the current item (key = id ?? type)
                  state.selectedItemIds = currentItem
                    ? [currentItem.id ?? currentItem.type]
                    : [];
                },
                undefined,
                "setCurrentItem"
              );
            },
            setSelection: (ids) => {
              set(
                (state) => {
                  state.selectedItemIds = ids;
                },
                undefined,
                "setSelection"
              );
            },
            toggleSelection: (id) => {
              set(
                (state) => {
                  const selected = state.selectedItemIds;
                  const index = selected.indexOf(id);
                  if (index === -1) {
                    selected.push(id);
                  } else {
                    selected.splice(index, 1);
                  }
                  // A group selection has no single "current item" — hide the
                  // per-item settings while more than one item is selected.
                  if (selected.length !== 1) {
                    state.templateState.currentItem = undefined;
                  }
                },
                undefined,
                "toggleSelection"
              );
            },
            clearSelection: () => {
              set(
                (state) => {
                  state.selectedItemIds = [];
                  state.templateState.currentItem = undefined;
                },
                undefined,
                "clearSelection"
              );
            },
            setIsInteracting: (isInteracting) => {
              set(
                (state) => {
                  state.isInteracting = isInteracting;
                },
                undefined,
                "setIsInteracting"
              );
            },
            updateLogo: (logo) => {
              set(
                (state) => {
                  const templateState = state.templateState;
                  templateState.logo = { ...templateState.logo, ...logo };
                  syncTemplateStateWithTemplates(state, templateState);
                },
                undefined,
                "updateLogo"
              );
            },
            updateHeader: (header) => {
              set(
                (state) => {
                  const templateState = state.templateState;
                  templateState.header = { ...templateState.header, ...header };
                  syncTemplateStateWithTemplates(state, templateState);
                },
                undefined,
                "updateHeader"
              );
            },
            updateParameteredText: (textItem) => {
              set(
                (state) => {
                  const templateState = state.templateState;
                  if (!templateState.parameteredTexts) return;
                  const pickedTextIndex =
                    templateState.parameteredTexts.findIndex(
                      (text) => text.id === textItem.id
                    );
                  if (
                    pickedTextIndex !== -1 &&
                    templateState.parameteredTexts[pickedTextIndex]
                  ) {
                    templateState.parameteredTexts[pickedTextIndex] = {
                      ...templateState.parameteredTexts[pickedTextIndex],
                      ...textItem,
                    };
                    syncTemplateStateWithTemplates(state, templateState);
                  }
                },
                undefined,
                "updateParameteredText"
              );
            },
            addParameteredTexts: (texts) => {
              set(
                (state) => {
                  const templateState = state.templateState;
                  templateState.parameteredTexts = [
                    ...(templateState.parameteredTexts ?? []),
                    ...texts,
                  ];
                  syncTemplateStateWithTemplates(state, templateState);
                },
                undefined,
                "addParameteredTexts"
              );
            },
            updateFigure: (figureItem, figureType) => {
              set(
                (state) => {
                  const templateState = state.templateState;
                  if (figureType === "number") {
                    const existingNumber = templateState.figure?.number;
                    templateState.figure = {
                      ...templateState.figure,
                      number: {
                        ...(existingNumber ?? ({} as ParameteredText)),
                        ...figureItem,
                      },
                    };
                  } else {
                    if (!templateState.figure) return;
                    templateState.figure.units = {
                      ...(templateState.figure.units ??
                        ({} as ParameteredText)),
                      ...figureItem,
                    };
                  }
                  syncTemplateStateWithTemplates(state, templateState);
                },
                undefined,
                "updateFigure"
              );
            },
            updateCustomText: (textItem) => {
              set(
                (state) => {
                  const templateState = state.templateState;
                  const pickedTextIndex = templateState.customTexts.findIndex(
                    (text) => text.id === textItem.id
                  );
                  if (
                    pickedTextIndex !== -1 &&
                    templateState.customTexts[pickedTextIndex]
                  ) {
                    templateState.customTexts[pickedTextIndex] = {
                      ...templateState.customTexts[pickedTextIndex],
                      ...textItem,
                    };
                    syncTemplateStateWithTemplates(state, templateState);
                  }
                },
                undefined,
                "updateCustomText"
              );
            },
            addCustomText: (customText) => {
              set(
                (state) => {
                  const templateState = state.templateState;
                  templateState.customTexts.push(customText);
                  templateState.currentItem = {
                    type: ITEM_TYPES.CustomText,
                    id: customText.id,
                  };
                  syncTemplateStateWithTemplates(state, templateState);
                },
                undefined,
                "addCustomText"
              );
            },
            deleteCustomText: (id) => {
              set(
                (state) => {
                  const templateState = state.templateState;
                  templateState.customTexts = templateState.customTexts.filter(
                    (text) => text.id !== id
                  );
                  if (templateState.currentItem?.id === id) {
                    templateState.currentItem = undefined;
                  }
                  syncTemplateStateWithTemplates(state, templateState);
                },
                undefined,
                "deleteCustomText"
              );
            },
            setCustomTexts: (customTexts) => {
              set(
                (state) => {
                  const templateState = state.templateState;
                  templateState.customTexts = customTexts;
                  syncTemplateStateWithTemplates(state, templateState);
                },
                undefined,
                "setCustomTexts"
              );
            },
            setMusic: (updatedMusic) => {
              set(
                (state) => {
                  const templateState = state.templateState;
                  templateState.music = updatedMusic ?? undefined;
                  syncTemplateStateWithTemplates(state, templateState);
                },
                undefined,
                "setMusic"
              );
            },
            updateShape: (shapeItem) => {
              set(
                (state) => {
                  const templateState = state.templateState;
                  const pickedShapeIndex = templateState.shapes.findIndex(
                    (shape) => shape.id === shapeItem.id
                  );
                  const pickedSlideLayoutShapeIndex =
                    templateState.slideLayoutShapes?.findIndex(
                      (shape) => shape.id === shapeItem.id
                    ) ?? -1;
                  if (
                    pickedShapeIndex !== -1 &&
                    templateState.shapes[pickedShapeIndex]
                  ) {
                    templateState.shapes[pickedShapeIndex] = {
                      ...templateState.shapes[pickedShapeIndex],
                      ...shapeItem,
                    };
                    syncTemplateStateWithTemplates(state, templateState);
                  } else if (
                    pickedSlideLayoutShapeIndex !== -1 &&
                    templateState.slideLayoutShapes?.[
                      pickedSlideLayoutShapeIndex
                    ]
                  ) {
                    templateState.slideLayoutShapes[
                      pickedSlideLayoutShapeIndex
                    ] = {
                      ...templateState.slideLayoutShapes[
                        pickedSlideLayoutShapeIndex
                      ],
                      ...shapeItem,
                    };
                    syncTemplateStateWithTemplates(state, templateState);
                  }
                },
                undefined,
                "updateShape"
              );
            },
            addShape: (shapeItem) => {
              set(
                (state) => {
                  const templateState = state.templateState;
                  templateState.shapes.push(shapeItem);
                  templateState.currentItem = {
                    type: ITEM_TYPES.Shape,
                    id: shapeItem.id,
                  };
                  syncTemplateStateWithTemplates(state, templateState);
                },
                undefined,
                "addShape"
              );
            },
            deleteShape: (id) => {
              set(
                (state) => {
                  const templateState = state.templateState;
                  const inShapes = templateState.shapes.some(
                    (shape) => shape.id === id
                  );
                  if (inShapes) {
                    templateState.shapes = templateState.shapes.filter(
                      (shape) => shape.id !== id
                    );
                  } else if (templateState.slideLayoutShapes) {
                    templateState.slideLayoutShapes =
                      templateState.slideLayoutShapes.filter(
                        (shape) => shape.id !== id
                      );
                  }
                  if (templateState.currentItem?.id === id) {
                    templateState.currentItem = undefined;
                  }
                  syncTemplateStateWithTemplates(state, templateState);
                },
                undefined,
                "deleteShape"
              );
            },
            setShapes: (shapesItem) => {
              set(
                (state) => {
                  const templateState = state.templateState;
                  templateState.shapes = shapesItem;
                  syncTemplateStateWithTemplates(state, templateState);
                },
                undefined,
                "setShapes"
              );
            },
            addSlideLayoutShape: (shapeItem) => {
              set(
                (state) => {
                  const templateState = state.templateState;
                  templateState.slideLayoutShapes ??= [];
                  templateState.slideLayoutShapes.push(shapeItem);
                  templateState.currentItem = {
                    type: ITEM_TYPES.Shape,
                    id: shapeItem.id,
                  };
                  syncTemplateStateWithTemplates(state, templateState);
                },
                undefined,
                "addSlideLayoutShape"
              );
            },
            deleteSlideLayoutShape: (id) => {
              set(
                (state) => {
                  const templateState = state.templateState;
                  if (templateState.slideLayoutShapes) {
                    templateState.slideLayoutShapes =
                      templateState.slideLayoutShapes.filter(
                        (shape) => shape.id !== id
                      );
                  }
                  if (templateState.currentItem?.id === id) {
                    templateState.currentItem = undefined;
                  }
                  syncTemplateStateWithTemplates(state, templateState);
                },
                undefined,
                "deleteSlideLayoutShape"
              );
            },
            updateTable: (update) => {
              set(
                (state) => {
                  const templateState = state.templateState;
                  templateState.table = {
                    ...(templateState.table ?? ({} as Table)),
                    ...update,
                  };
                  syncTemplateStateWithTemplates(state, templateState);
                },
                undefined,
                "updateTable"
              );
            },
            updateTableTextStyle: (style) => {
              set(
                (state) => {
                  const templateState = state.templateState;
                  if (templateState.table) {
                    templateState.table.textStyle = {
                      ...templateState.table.textStyle,
                      ...style,
                    };
                    syncTemplateStateWithTemplates(state, templateState);
                  }
                },
                undefined,
                "updateTableTextStyle"
              );
            },
            updateTableDividerStyle: (style) => {
              set(
                (state) => {
                  const templateState = state.templateState;
                  if (templateState.table?.dividerStyle) {
                    templateState.table.dividerStyle = {
                      ...templateState.table.dividerStyle,
                      ...style,
                    };
                    syncTemplateStateWithTemplates(state, templateState);
                  }
                },
                undefined,
                "updateTableDividerStyle"
              );
            },
            remapBrandKit: (params) =>
              set(
                (state) => {
                  state.defaultChapter = params.defaultChapter;
                  state.defaultTransition = params.defaultTransition;
                  state.defaultIntro = params.defaultIntro;
                  state.defaultClosing = params.defaultClosing;

                  params.remappedTemplates.forEach((template, id) => {
                    state.templates.set(id, template);
                  });

                  const currentTemplateId =
                    state.currentSlide ?? state.currentChapter.templateId;
                  if (
                    currentTemplateId &&
                    params.remappedTemplates.has(currentTemplateId)
                  ) {
                    state.templateState = {
                      ...params.remappedTemplates.get(currentTemplateId)!,
                      currentItem: state.templateState.currentItem,
                    };
                  }
                },
                undefined,
                "remapBrandKit"
              ),
          };
        }),
        {
          limit: 500,
          partialize: (state) => ({
            currentChapter: state.currentChapter,
            currentSlide: state.currentSlide,
            templateState: state.templateState,
            templates: state.templates,
            slideNameToTemplateIdMap: state.slideNameToTemplateIdMap,
            defaultChapter: state.defaultChapter,
            defaultTransition: state.defaultTransition,
            defaultIntro: state.defaultIntro,
            defaultClosing: state.defaultClosing,
          }),
          // zundo records a snapshot on EVERY set() unless this says nothing
          // changed. Compare the tracked (partialized) fields by reference — with
          // immer's structural sharing, an action that only mutates a non-tracked
          // field leaves these references intact, so it isn't recorded as an
          // undo step. This excludes transient actions like `setIsInteracting`
          // (mutates `isInteracting`) and `setServerTemplate` (mutates
          // `serverTemplates`), neither of which is part of `partialize`.
          equality: (a, b) =>
            a.currentChapter === b.currentChapter &&
            a.currentSlide === b.currentSlide &&
            a.templateState === b.templateState &&
            a.templates === b.templates &&
            a.slideNameToTemplateIdMap === b.slideNameToTemplateIdMap &&
            a.defaultChapter === b.defaultChapter &&
            a.defaultTransition === b.defaultTransition &&
            a.defaultIntro === b.defaultIntro &&
            a.defaultClosing === b.defaultClosing,
        }
      ),
      { name: "TemplateEditorStore" }
    )
  );
};

const changeTemplatesAspectRatioCallback = (
  state: TemplateEditorState,
  newAspectRatio: number,
  previewSizes: Size
) => {
  state.templateState = adjustTemplateToAspectRatioChange(
    state.templateState,
    newAspectRatio,
    previewSizes
  );
  state.templates.forEach((template, templateId) => {
    state.templates.set(
      templateId,
      adjustTemplateToAspectRatioChange(template, newAspectRatio, previewSizes)
    );
  });
  state.defaultChapter = adjustTemplateToAspectRatioChange(
    state.defaultChapter,
    newAspectRatio,
    previewSizes
  );
  state.defaultTransition = adjustTemplateToAspectRatioChange(
    state.defaultTransition,
    newAspectRatio,
    previewSizes
  );
  state.defaultIntro = adjustTemplateToAspectRatioChange(
    state.defaultIntro,
    newAspectRatio,
    previewSizes
  );
  state.defaultClosing = adjustTemplateToAspectRatioChange(
    state.defaultClosing,
    newAspectRatio,
    previewSizes
  );
};

const syncTemplateStateWithTemplates = (
  state: TemplateEditorState,
  newTemplateState: TemplateState
) => {
  // Always commit the active state, even if the templates cache isn't ready yet.
  state.templateState = newTemplateState;

  const templateId = state.currentSlide
    ? state.currentSlide
    : state.currentChapter.templateId;
  if (!templateId) return;
  if (!state.templates.has(templateId)) return;
  state.templates.set(templateId, newTemplateState);
};

const applyPresenterContactToOtherTemplates = (
  state: TemplateEditorState,
  newPresenterPartial: Partial<Avatar>,
  mediaAspectRatio?: number,
  templateIdFilter?: Set<string>
) => {
  const { presenter, contactId } = newPresenterPartial;
  if (!presenter && !contactId) return;

  const currentTemplateId = state.currentSlide
    ? state.currentSlide
    : state.currentChapter.templateId;

  const sharedPresenterFields: Partial<Avatar> = {
    ...("presenter" in newPresenterPartial && { presenter }),
    ...(contactId !== undefined && { contactId }),
  };
  // Derive the new avatar's image ratio from the source chapter's (already
  // resized) avatar so other chapters can be recomputed against it. The inverse
  // of calculateNewItemSizeAndPosition: ratio = (width * aspect) / height.
  const presenterImageRatio =
    presenter !== undefined &&
    newPresenterPartial.size != null &&
    mediaAspectRatio != null &&
    newPresenterPartial.size.height > 0
      ? (newPresenterPartial.size.width * mediaAspectRatio) /
        newPresenterPartial.size.height
      : undefined;

  state.templates.forEach((template, templateId) => {
    if (templateId === currentTemplateId) return;
    if (templateIdFilter && !templateIdFilter.has(templateId)) return;
    if (template.presenter.presenter === undefined) return;

    // Recompute each chapter's avatar layout for the new avatar's image ratio,
    // preserving centering/bottom anchoring the same way the current chapter's
    // avatar is resized on change (see computeAvatarSizing).
    const { size, position } =
      presenterImageRatio != null && mediaAspectRatio != null
        ? computeAvatarLayoutForRatio(
            template.presenter.size,
            template.presenter.position,
            presenterImageRatio,
            mediaAspectRatio
          )
        : {
            size: template.presenter.size,
            position: template.presenter.position,
          };

    template.presenter = {
      ...template.presenter,
      ...sharedPresenterFields,
      size,
      position,
    };
  });
};

// Store-side equivalent of useChangeLayer's newHighestLayerIndex, for a non-current template.
const getNextLayerIndex = (template: TemplateState): number =>
  Math.max(
    0,
    ...[
      template.header.background ? template.header : undefined,
      template.logo.showLogo ? template.logo : undefined,
      ...getLayerableCollectionItems(template),
      template.textStyle.showSubtitles ? template.textStyle : undefined,
      template.titleStyle.showTitle ? template.titleStyle : undefined,
    ]
      .filter((item) => item !== undefined)
      .map((item) => item.layerIndex)
  ) + 1;

const applyPresenterToCurrentTemplate = (
  state: TemplateEditorState,
  newPresenterPartial: Partial<Avatar>
) => {
  const templateState = state.templateState;
  const newPresenter = {
    ...templateState.presenter,
    ...newPresenterPartial,
  };
  if (!isEqual(templateState.presenter, newPresenter)) {
    templateState.presenter = newPresenter;
    syncTemplateStateWithTemplates(state, templateState);
  }
};

const selectDefaultTemplateByType = (
  state: TemplateEditorState,
  templateType: TemplateType
) => {
  switch (templateType) {
    case TemplateType.CHAPTER:
      return state.defaultChapter;
    case TemplateType.TRANSITION:
      return state.defaultTransition;
    case TemplateType.INTRODUCTION:
      return state.defaultIntro;
    case TemplateType.CLOSING:
      return state.defaultClosing;
  }
};

export function useTemplateEditorStore<T>(
  selector: (state: TemplateEditorState) => T
): T {
  const store = useContext(TemplateEditorStoreContext);
  if (!store) {
    throw new Error(
      "useTemplateEditorStore must be used within a TemplateDataProvider"
    );
  }
  return useStore(store, selector);
}

// Imperative API for accessing the store (for callbacks)
export function useTemplateEditorStoreApi() {
  const store = useContext(TemplateEditorStoreContext);
  if (!store) {
    throw new Error(
      "useTemplateEditorStoreApi must be used within a TemplateDataProvider"
    );
  }
  return store;
}

// Hook for accessing temporal (undo/redo) functionality
export function useTemplateEditorTemporal() {
  const store = useContext(TemplateEditorStoreContext);
  if (!store) {
    throw new Error(
      "useTemplateEditorTemporal must be used within a TemplateDataProvider"
    );
  }
  return useStore(store.temporal);
}
